<template>
    <section>
        <div class="events">
            <div class="add-event-container">
                <input 
                    type="text" 
                    class="form-control event-input" 
                    v-model="newEventName" 
                    maxlength="100"
                    placeholder="Nome do novo evento"
                    @keyup.enter="saveEvent">
                <button class="btn save-event" @click="saveEvent">
                    <i class="fas fa-plus"></i>
                    Adicionar
                </button>
            </div>
            <div class="events-table-container">
                <table class="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th @click="sortBy('created_at')" class="sortable">
                                Criado em
                                <i :class="getSortIcon('created_at')"></i>
                            </th>
                            <th @click="sortBy('updated_at')" class="sortable">
                                Alterado em
                                <i :class="getSortIcon('updated_at')"></i>
                            </th>
                            <th @click="sortBy('user_name')" class="sortable">
                                Criado por
                                <i :class="getSortIcon('user_name')"></i>
                            </th>
                            <th @click="sortBy('name')" class="sortable">
                                Nome
                                <i :class="getSortIcon('name')"></i>
                            </th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="event in sortedEventsList" :key="event.id">
                            <td>{{ formatDate(event.created_at) }}</td>
                            <td>{{ formatDate(event.updated_at) }}</td>
                            <td>{{ event.user_name }}</td>
                            <td class="event-name">{{ event.name }}</td>
                            <td>
                                <span 
                                    :class="['badge', event.active ? 'badge-success' : 'badge-secondary badge-inactive']"
                                    :title="event.active ? '' : 'Clique para ativar'"
                                    @click="!event.active && activateEvent(event)">
                                    <span>{{ event.active ? 'Ativo' : 'Inativo' }}</span>
                                </span>
                            </td>
                            <td class="actions">
                                <button class="btn btn-sm btn-warning" title="Renomear" @click="renameEvent(event)">
                                    <i class="fas fa-edit"></i> Renomear
                                </button>
                                <button class="btn btn-sm btn-danger" title="Excluir" @click="deleteEvent(event)">
                                    <i class="fas fa-trash"></i> Excluir
                                </button>
                            </td>
                        </tr>
                        <tr v-if="eventsList.length === 0">
                            <td colspan="6" class="text-center text-muted">
                                Nenhum evento cadastrado
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </section>
</template>

<script>
    export default {
        name: 'EventsList',
        props: {
            eventsList: {
                type: Array,
                default: []
            }
        },
        data() {
            return {
                newEventName: '',
                sortColumn: null,
                sortDirection: 'asc'
            }
        },
        computed: {
            sortedEventsList() {
                let sorted = [...this.eventsList];

                // Sempre manter eventos ativos no topo
                sorted.sort((a, b) => {
                    // Primeiro critério: ativo no topo
                    if (a.active !== b.active) {
                        return b.active - a.active;
                    }

                    // Segundo critério: ordenação escolhida pelo usuário
                    if (this.sortColumn) {
                        let aValue = a[this.sortColumn];
                        let bValue = b[this.sortColumn];

                        // Tratamento para datas
                        if (this.sortColumn === 'created_at' || this.sortColumn === 'updated_at') {
                            aValue = aValue ? new Date(aValue).getTime() : 0;
                            bValue = bValue ? new Date(bValue).getTime() : 0;
                        }

                        // Tratamento para strings
                        if (typeof aValue === 'string') {
                            aValue = aValue.toLowerCase();
                            bValue = bValue.toLowerCase();
                        }

                        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
                        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
                    }

                    return 0;
                });

                return sorted;
            }
        },
        methods: {
            formatDate(date) {
                if (!date) return '-';
                return new Date(date).toLocaleString('pt-BR');
            },

            sortBy(column) {
                if (this.sortColumn === column) {
                    // Inverte a direção se clicar na mesma coluna
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    // Nova coluna, começa com ascendente
                    this.sortColumn = column;
                    this.sortDirection = 'asc';
                }
            },

            getSortIcon(column) {
                if (this.sortColumn !== column) {
                    return 'fas fa-sort sort-icon';
                }
                return this.sortDirection === 'asc' 
                    ? 'fas fa-sort-up sort-icon active' 
                    : 'fas fa-sort-down sort-icon active';
            },

            checkDuplicateName(name, excludeId = null) {
                return this.eventsList.some(event => 
                    event.name.toLowerCase() === name.toLowerCase() && 
                    event.id !== excludeId
                );
            },

            async saveEvent() {
                if (!this.newEventName.trim()) {
                    Swal.fire({
                        type: 'warning',
                        title: 'Campo obrigatório',
                        text: 'Por favor, informe o nome do evento.',
                        confirmButtonColor: '#1c869a'
                    });
                    return;
                }

                if (this.checkDuplicateName(this.newEventName)) {
                    Swal.fire({
                        type: 'error',
                        title: 'Nome duplicado',
                        text: 'Já existe um evento com este nome. Por favor, escolha um nome único.',
                        confirmButtonColor: '#1c869a'
                    });
                    return;
                }

                try {
                    const response = await axios.post('events/create', {
                        name: this.newEventName.trim()
                    });

                    Swal.fire({
                        type: 'success',
                        title: 'Sucesso!',
                        text: 'Evento criado com sucesso.',
                        timer: 2000,
                        showConfirmButton: false
                    });

                    this.newEventName = '';
                    this.$emit('refresh-events');
                } catch (error) {
                    Swal.fire({
                        type: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao criar evento.',
                        confirmButtonColor: '#1c869a'
                    });
                }
            },

            async renameEvent(event) {
                const { value: newName } = await Swal.fire({
                    title: 'Renomear Evento',
                    input: 'text',
                    inputLabel: 'Novo nome do evento',
                    inputValue: event.name,
                    showCancelButton: true,
                    cancelButtonText: 'Cancelar',
                    confirmButtonText: 'Renomear',
                    confirmButtonColor: '#1c869a',
                    inputValidator: (value) => {
                        if (!value) {
                            return 'O nome não pode estar vazio!';
                        }
                    }
                });

                if (newName && newName.trim() !== event.name) {
                    if (this.checkDuplicateName(newName, event.id)) {
                        Swal.fire({
                            type: 'error',
                            title: 'Nome duplicado',
                            text: 'Já existe um evento com este nome. Por favor, escolha um nome único.',
                            confirmButtonColor: '#1c869a'
                        });
                        return;
                    }

                    try {
                        await axios.put(`events/update/${event.id}`, {
                            name: newName.trim()
                        });

                        Swal.fire({
                            type: 'success',
                            title: 'Sucesso!',
                            text: 'Evento renomeado com sucesso.',
                            timer: 2000,
                            showConfirmButton: false
                        });

                        this.$emit('refresh-events');
                    } catch (error) {
                        Swal.fire({
                            type: 'error',
                            title: 'Erro',
                            text: error.response?.data?.message || 'Erro ao renomear evento.',
                            confirmButtonColor: '#1c869a'
                        });
                    }
                }
            },

            async deleteEvent(event) {
                // Validação 1: Não pode excluir se for o único evento
                if (this.eventsList.length === 1) {
                    Swal.fire({
                        type: 'error',
                        title: 'Operação não permitida',
                        text: 'Não é possível excluir todos os eventos. Deve haver pelo menos um evento cadastrado no sistema.',
                        confirmButtonColor: '#1c869a'
                    });
                    return;
                }

                // Validação 2: Não pode excluir evento ativo
                if (event.active) {
                    Swal.fire({
                        type: 'error',
                        title: 'Evento ativo',
                        html: 'Não é possível excluir o evento <strong>' + event.name + '</strong> porque ele está ativo.<br><br>' +
                              '<small class="text-muted">Ative outro evento antes de excluir este.</small>',
                        confirmButtonColor: '#1c869a'
                    });
                    return;
                }

                const result = await Swal.fire({
                    title: 'Confirmar exclusão',
                    html: `Deseja realmente excluir o evento <strong>${event.name}</strong>?`,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#dc3545',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Sim, excluir!',
                    cancelButtonText: 'Cancelar'
                });

                if (result.value) {
                    try {
                        await axios.delete(`events/delete/${event.id}`);

                        Swal.fire({
                            type: 'success',
                            title: 'Excluído!',
                            text: 'Evento excluído com sucesso.',
                            timer: 2000,
                            showConfirmButton: false
                        });

                        this.$emit('refresh-events');
                    } catch (error) {
                        Swal.fire({
                            type: 'error',
                            title: 'Erro',
                            text: error.response?.data?.message || 'Erro ao excluir evento.',
                            confirmButtonColor: '#1c869a'
                        });
                    }
                }
            },

            async activateEvent(event) {
                const result = await Swal.fire({
                    title: 'Ativar Evento',
                    html: `Deseja ativar o evento <strong>${event.name}</strong>?<br><br>
                           <small class="text-muted">Apenas um evento pode estar ativo por vez. 
                           O evento atualmente ativo será desativado automaticamente.</small>`,
                    type: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#28a745',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Sim, ativar!',
                    cancelButtonText: 'Cancelar'
                });

                if (result.value) {
                    try {
                        await axios.put(`events/activate/${event.id}`);

                        Swal.fire({
                            type: 'success',
                            title: 'Ativado!',
                            text: 'Evento ativado com sucesso.',
                            timer: 2000,
                            showConfirmButton: false
                        });

                        this.$emit('refresh-events');
                    } catch (error) {
                        Swal.fire({
                            type: 'error',
                            title: 'Erro',
                            text: error.response?.data?.message || 'Erro ao ativar evento.',
                            confirmButtonColor: '#1c869a'
                        });
                    }
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .events {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;

        .add-event-container {
            display: flex;
            gap: 15px;
            margin-bottom: 30px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            .event-input {
                flex: 1;
                border: 1px solid #ced4da;
                border-radius: 4px;
                padding: 10px 15px;
                font-size: 14px;
                transition: border-color 0.3s;

                &:focus {
                    border-color: #007bff;
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
                }

                &::placeholder {
                    color: #6c757d;
                }
            }

            .save-event {
                min-width: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                font-weight: 500;
                border-radius: 4px !important;
                transition: all 0.3s;
                background-color: #1c869a;
                color: white;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
                    background-color: #196d7e;
                }

                i {
                    font-size: 14px;
                }
            }
        }

        .events-table-container {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: auto;
            max-height: 60vh;

            .table {
                margin-bottom: 0;
                text-align: center;

                thead {
                    background: linear-gradient(135deg, #1c869a 0%, #187283 100%);

                    tr th {
                        color: white;
                        font-weight: 600;
                        text-transform: uppercase;
                        font-size: 12px;
                        letter-spacing: 0.5px;
                        border: none;
                        padding: 15px;

                        &.sortable {
                            cursor: pointer;
                            user-select: none;
                            transition: all 0.2s;

                            &:hover {
                                background-color: rgba(255, 255, 255, 0.1);
                            }

                            .sort-icon {
                                margin-left: 5px;
                                font-size: 10px;
                                opacity: 0.5;
                                transition: opacity 0.2s;

                                &.active {
                                    opacity: 1;
                                }
                            }
                        }
                    }
                }

                tbody {
                    tr {
                        transition: all 0.2s;

                        &:hover {
                            background-color: #e4e5e6;
                        }

                        td {
                            padding: 12px 15px;
                            vertical-align: middle;
                            font-size: 14px;
                            border-top: 1px solid #dee2e6;

                            &.event-name {
                                font-weight: 500;
                                color: #495057;
                            }

                            .badge {
                                padding: 5px 12px;
                                font-size: 11px;
                                font-weight: 600;
                                border-radius: 12px;
                                transition: all 0.3s;

                                &.badge-inactive {
                                    cursor: pointer;
                                    position: relative;

                                    span {
                                        transition: opacity 0.3s;
                                    }

                                    &:hover {
                                        background-color: #1c869a !important;
                                        transform: scale(1.05);

                                        span {
                                            opacity: 0;
                                        }

                                        &::after {
                                            content: 'ATIVAR';
                                            position: absolute;
                                            top: 50%;
                                            left: 50%;
                                            transform: translate(-50%, -50%);
                                        }
                                    }
                                }
                            }

                            &.actions {
                                .btn {
                                    padding: 5px 10px;
                                    transition: all 0.2s;
                                    margin: 0 2px;
                                    border-radius: 4px !important;
                                    font-size: 11px;
                                    font-weight: 600;

                                    &:hover {
                                        transform: translateY(-2px);
                                    }

                                    i {
                                        font-size: 12px;
                                    }
                                }

                                .btn-warning {
                                    background-color: #326ba9;
                                    color: white;
                                    border-color: #326ba9;
                                }
                            }

                            &.text-muted {
                                padding: 40px 15px;
                                font-style: italic;
                            }
                        }
                    }
                }
            }
        }
    }

    // Responsivo
    @media (max-width: 768px) {
        .events {
            padding: 10px;

            .add-event-container {
                flex-direction: column;

                .save-event {
                    width: 100%;
                }
            }

            .events-table-container {
                overflow-x: auto;

                .table {
                    font-size: 12px;

                    thead tr th,
                    tbody tr td {
                        padding: 8px;
                    }
                }
            }
        }
    }
</style>